/**
 * LoadingIndicator
 * Renders a loading animation whose style adapts to ANIMATION_MODE.
 *
 * Variants:
 *   "dots"    – StatusBar bouncing dots  → pulse dots  → hidden
 *   "spinner" – MUI CircularProgress     → harvey ball → hidden
 *   "cursor"  – Blinking text cursor     → static bar  → hidden
 *
 * Props:
 *   variant  – "dots" | "spinner" | "cursor"  (default "spinner")
 *   size     – pixel size (default 12 for spinner, 7 for dots)
 *   color    – CSS color string (default '#2C365E')
 *   sx       – additional Box sx overrides
 */

import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/system';
import ANIMATION_MODE from '../config/animationMode';

// ── Keyframes ────────────────────────────────────────────────

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-7px); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

const harveyRotate = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// ── Dots (StatusBar) ─────────────────────────────────────────

const LiveDots = ({ color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    {[0, 0.18, 0.36].map((delay, i) => (
      <Box
        key={i}
        sx={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: color,
          animation: `${bounce} 1.1s ease-in-out infinite`,
          animationDelay: `${delay}s`,
        }}
      />
    ))}
  </Box>
);

const RecordingDots = ({ color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    {[0, 0.6, 1.2].map((delay, i) => (
      <Box
        key={i}
        sx={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: color,
          animation: `${pulse} 2s ease-in-out infinite`,
          animationDelay: `${delay}s`,
        }}
      />
    ))}
  </Box>
);

// ── Spinner / Harvey Ball (ToolCallCard, ChartDisplay) ───────

const HarveyBall = ({ size, color }) => (
  <Box
    sx={{
      width: size, height: size, borderRadius: '50%',
      background: `conic-gradient(${color} 0deg, ${color} 90deg, transparent 90deg, transparent 360deg)`,
      animation: `${harveyRotate} 3s linear infinite`,
      flexShrink: 0,
    }}
  />
);

// ── Cursor (streaming text cursor) ───────────────────────────

const LiveCursor = ({ color, sx: sxProp }) => {
  const blink = keyframes`
    0%, 100% { opacity: 0.6; }
    50% { opacity: 0; }
  `;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', width: 6, height: '1.1em',
        ml: 0.25, verticalAlign: 'text-bottom',
        backgroundColor: color, opacity: 0.6,
        animation: `${blink} 1s step-end infinite`,
        ...sxProp,
      }}
    />
  );
};

const RecordingCursor = ({ color, sx: sxProp }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block', width: 6, height: '1.1em',
      ml: 0.25, verticalAlign: 'text-bottom',
      backgroundColor: color, opacity: 0.5,
      ...sxProp,
    }}
  />
);

// ── Main export ──────────────────────────────────────────────

const LoadingIndicator = ({
  variant = 'spinner',
  size,
  color = '#2C365E',
  thickness,
  sx: sxProp,
}) => {
  if (ANIMATION_MODE === 'hidden') return null;

  if (variant === 'dots') {
    return ANIMATION_MODE === 'recording'
      ? <RecordingDots color={color} />
      : <LiveDots color={color} />;
  }

  if (variant === 'cursor') {
    return ANIMATION_MODE === 'recording'
      ? <RecordingCursor color={color} sx={sxProp} />
      : <LiveCursor color={color} sx={sxProp} />;
  }

  // variant === 'spinner' (default)
  const s = size || 12;
  return ANIMATION_MODE === 'recording'
    ? <HarveyBall size={s} color={color} />
    : <CircularProgress size={s} thickness={thickness || 5} sx={{ color, flexShrink: 0, ...sxProp }} />;
};

export default LoadingIndicator;

// Re-export mode so consumers can check it directly (e.g. for shimmer vs static text)
export { ANIMATION_MODE };
